import { isEmpty } from 'lodash';
import React, { JSX, useEffect, useRef, useState } from 'react';
import { unstable_batchedUpdates } from 'react-dom';

import { track } from '~/src/dash/analytics/analytics';
import {
  createReport,
  deleteReport,
  IReport,
  queryReports,
  updateReport,
} from '~/src/dash/api/console-api';
import { getReportTypes } from '~/src/dash/apiV2/reporting/reportTypes';
import { ISelectOption } from '~/src/dash/components/dropdowns/common';
import { FilterRowV2 } from '~/src/dash/components/filters/FilterRowV2';
import { SearchResultsText } from '~/src/dash/components/input/common';
import { ListViewTable } from '~/src/dash/components/tables/ListViewTable';
import {
  extractSearchableColumnKeys,
  getComparator,
  IListViewTableColumnInfo,
  IListViewTableColumns,
  IListViewTableRow,
  Order,
  RowsPerPage,
  stableSort,
} from '~/src/dash/components/tables/ListViewTable/common';
import {
  ButtonV2,
  Col,
  Cols,
  Panel,
  PanelBreak,
  Panels,
  Row,
  Rows,
  spacing,
  TextInput,
  usePublicaTranslation,
} from '~/src/dash/publicaUI';
import {
  extractRouteParams,
  getRoutePath,
  navigate,
  RouteReportExisting,
  RouteReportNew,
} from '~/src/dash/router/routes';
import {
  LoadingState,
  LoadingStateHasData,
  LoadingStateLoading,
  LoadingStateNoData,
} from '~/src/dash/utils/loading-states';
import { IS_AUTHORIZED } from '~/src/dash/utils/permission_service';
import { ROLE_ID_SUPER } from '~/src/dash/utils/roles';
import { isDemoAccount } from '~/src/dash/utils/ui';
import { useBaseConfig, useBaseStore } from '~/src/dash/views/base/contexts';
import { ReportingListContext } from '~/src/dash/views/reporting/ReportingListView/context';
import { SnackbarView } from '~/src/dash/views/snackbar-view';
import { TopBar } from '~/src/dash/views/topbar/TopBar';

const generateReportTypeIDToLabelMap = (
  reportTypeOptions: ISelectOption[],
): Record<number, string> => {
  const map: Record<number, string> = {};
  reportTypeOptions.forEach((option) => {
    map[Number(option.id)] = option.label;
  });
  return map;
};

const queryFilteredData = (
  filteredData: IReport[],
  query: string,
  searchableColumnKeys: (keyof IReport)[],
): IReport[] => {
  let queriedRows = [...filteredData];
  if (query !== '') {
    queriedRows = filteredData.filter((row) => {
      for (const key of searchableColumnKeys) {
        if (row[key]?.toString().toLowerCase().includes(query.toLowerCase())) {
          return true;
        }
      }
      return false;
    });
  }
  return queriedRows;
};

const formatReportsResponseIntoLVTRows = (
  reports: IReport[],
  columns: IListViewTableColumns,
): IListViewTableRow[] => {
  const formattedRows = [] as IListViewTableRow[];
  reports.forEach((report) => {
    const formattedRow = {} as IListViewTableRow;
    Object.values(columns).forEach((col) => {
      const { cellJSX, cellRenderType, cellStyle, formatValue, isNumeric, key, responseKey } = col;
      // ensure value is correct
      let value: unknown = report[responseKey as keyof IReport];
      if (isNumeric) {
        value = Number(value);
      }
      if (formatValue) {
        value = formatValue(value);
      }
      formattedRow[key] = {
        value,
        ...(cellJSX ? { jsx: cellJSX } : {}),
        ...(cellRenderType ? { renderType: cellRenderType } : {}),
        ...(cellStyle ? { cellStyle } : {}),
      };
    });
    formattedRows.push(formattedRow);
  });
  return formattedRows;
};

const scheduleOptionsList: ISelectOption[] = [
  { id: 0, label: 'Show All', tKey: 'ShowAll' },
  { id: 2, label: 'Scheduled', tKey: 'Scheduled' },
  { id: 1, label: 'Not Scheduled', tKey: 'NotScheduled' },
];

const generateReportTypeOptionsList = (publisherID: string): Promise<ISelectOption[]> => {
  return getReportTypes(publisherID)
    .then((reportTypes) => {
      const options = reportTypes.map((reportType) => {
        return {
          id: reportType.ID.toString(),
          label: reportType.Label + (reportType.Category === 1 ? ' Quick' : ''),
        };
      });
      // Sort options: Category 1 (Quick) reports first alphabetically, then others alphabetically
      options.sort((a, b) => {
        const aIsQuick = a.label.includes(' Quick');
        const bIsQuick = b.label.includes(' Quick');

        if (aIsQuick && !bIsQuick) return -1;
        if (!aIsQuick && bIsQuick) return 1;

        return a.label.localeCompare(b.label);
      });
      return options;
    })
    .catch((err) => {
      console.error(err);
      return [] as ISelectOption[];
    });
};

const generateUserOptionsList = (reports: IReport[], currentUserID: string): ISelectOption[] => {
  const uniqueUsers = new Set<string>([
    '-1', // All Users
    currentUserID.toString(), // Current User ID
  ]);

  const defaultUserOptions: ISelectOption[] = [
    {
      id: '-1',
      label: 'All Users',
      tKey: 'AllUsers',
    },
    {
      id: currentUserID.toString(),
      label: 'Current User',
      tKey: 'CurrentUser',
    },
  ];

  const userOptions: ISelectOption[] = [];

  reports.forEach((report) => {
    if (!report.User || uniqueUsers.has(report.User.ID)) return;

    if (report.User.RoleID === ROLE_ID_SUPER) {
      if (!uniqueUsers.has('0')) {
        uniqueUsers.add('0');
        userOptions.push({ id: '0', label: 'Publica Admin' });
      }
    } else {
      uniqueUsers.add(report.User.ID);
      userOptions.push({
        id: report.User.ID,
        label: `${report.User.FirstName} ${report.User.LastName}`,
      });
    }
  });

  return [...defaultUserOptions, ...userOptions.sort((a, b) => a.label.localeCompare(b.label))];
};

// IMPORTANT NOTES
// filters are also custom populated and thus cannot be simply updated via config.
// you must also update the populateFiltersAndData, handleApplySelect, handleClearFilters functions

export const ReportingListView = (): JSX.Element => {
  const { user, session, isSuperUser } = useBaseStore();
  const { t } = usePublicaTranslation();
  const readAuthorization = IS_AUTHORIZED(['read_report'], user.user);
  const writeAuthorization = IS_AUTHORIZED(['write_report'], user.user);
  const isDemoUser = isDemoAccount(user.user.Username);

  const { Reporting } = useBaseConfig();
  const cfg = Reporting.Main;
  const { cypressClassName, defaultState, columns } = cfg;

  const isMounted = useRef(false);

  // states
  const [loadState, setLoadState] = useState<LoadingState>(LoadingStateLoading);
  const [responseData, setResponseData] = useState<IReport[]>([]);
  const [filteredData, setFilteredData] = useState<IReport[]>([]);
  const [queriedData, setQueriedData] = useState<IReport[]>([]);
  const [totalRowCount, setTotalRowCount] = useState(0);
  const [isTableActionPending, setIsTableActionPending] = useState(false);
  const [sortedRows, setSortedRows] = useState([] as IListViewTableRow[]);
  const [visibleRows, setVisibleRows] = useState([] as IListViewTableRow[]);
  const [query, setQuery] = React.useState<string>('');
  const [filters, setFilters] = useState<Record<string, ISelectOption[]>>({});
  const [selectedFilters, setSelectedFilters] = useState<Record<string, Set<string | number>>>({});
  const [filtersLoadState, setFiltersLoadState] = useState<LoadingState>(LoadingStateLoading);
  const [reportTypeMap, setReportTypeMap] = useState<Record<number, string>>({});
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarError, setSnackbarError] = useState(false);
  const [order, setOrder] = useState<Order>(defaultState.order || 'asc');
  const [orderBy, setOrderBy] = useState<string>(defaultState.orderBy || 'Name');
  const [page, setPage] = useState(defaultState.page || 0);
  const [rowsPerPage, setRowsPerPage] = useState(defaultState.rowsPerPage || 10);

  const searchableColumnKeys = extractSearchableColumnKeys(columns) as (keyof IReport)[];

  const populateFiltersAndData = (reports: IReport[]): void => {
    const filterOptions: Record<string, ISelectOption[]> = {};
    const tempSelectedFilters: Record<string, Set<string | number>> = {};
    Promise.all([
      generateUserOptionsList(reports, user.user.ID),
      generateReportTypeOptionsList(session.publisher.ID),
    ])
      .then((result) => {
        filterOptions.User = result[0];
        filterOptions.Schedule = scheduleOptionsList;
        filterOptions.ReportType = result[1];
        const currentReportTypeMap = generateReportTypeIDToLabelMap(filterOptions.ReportType);

        Object.values(cfg.filters).forEach((filter) => {
          if (filter.key === 'User') {
            tempSelectedFilters.User = filter?.selectedOptionIDs || new Set([user.user.ID]);
          } else {
            tempSelectedFilters[filter.key] =
              filter?.selectedOptionIDs || new Set<string | number>();
          }
        });

        // Add other filters as needed
        unstable_batchedUpdates(() => {
          setFilters(filterOptions);
          setFiltersLoadState(LoadingStateHasData);
          setResponseData(reports);
          setReportTypeMap(currentReportTypeMap);
          setSelectedFilters(tempSelectedFilters);
        });
      })
      .catch((err) => {
        console.error(err);
        unstable_batchedUpdates(() => {
          setResponseData(reports);
          setLoadState(LoadingStateHasData);
          setFiltersLoadState(LoadingStateNoData);
          setTotalRowCount(0);
          setFilters({});
          setSnackbarError(true);
          setSnackbarMessage(t('SnackbarErrorRetrievingItem', { item: t('Filters') }));
          setReportTypeMap({});
          setSelectedFilters({});
        });
      });
  };

  // handles
  const handleSnackbarClose = (): void => {
    setSnackbarMessage('');
  };

  // this sets up the changes to force a re-sort
  const handleOnClickColumnHeader = (column: IListViewTableColumnInfo): void => {
    const { key, defaultOrderDirection } = column;
    if (orderBy !== key) {
      unstable_batchedUpdates(() => {
        setOrder(defaultOrderDirection || 'asc');
        setOrderBy(key);
        setPage(0);
      });
    } else {
      const isAsc = orderBy === key && order === 'asc';
      unstable_batchedUpdates(() => {
        setOrder(isAsc ? 'desc' : 'asc');
        setOrderBy(key);
        setPage(0);
      });
    }
  };

  const handleChangePage = (_e: unknown, newPage: number): void => {
    setPage(newPage);
    cfg.defaultState.page = newPage;
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>): void => {
    const newRowsPerPage = Number(event.target.value) as RowsPerPage;
    unstable_batchedUpdates(() => {
      setRowsPerPage(newRowsPerPage);
    });
    cfg.defaultState.rowsPerPage = newRowsPerPage;
  };

  const handleOnChangeQuery = (text: string): void => {
    track('Reporting List - Search', { isSuperUser }, user.user);
    unstable_batchedUpdates(() => {
      setLoadState(LoadingStateLoading);
      setQuery(text);
      setPage(0);
    });
  };

  const handleOnChangeStatus = (id: string, checked: boolean, _row: IListViewTableRow): void => {
    setIsTableActionPending(true);
    const selectedReport = responseData.find((report) => report.ID === id);
    if (selectedReport) {
      selectedReport.Active = checked ? 'true' : 'false';
      updateReport({
        query: {
          publisher_id: session.publisher.ID,
          report_id: selectedReport.ID,
          report: selectedReport,
        },
      })
        .then((updatedReport: IReport) => {
          track('Reporting List - Update Report Schedule Status', { isSuperUser }, user.user);
          unstable_batchedUpdates(() => {
            setResponseData((prevData) =>
              prevData.map((report) => (report.ID === updatedReport.ID ? updatedReport : report)),
            );
            setSnackbarMessage(
              t('SnackbarSuccessfullyUpdatedIDName', {
                id: updatedReport.ID,
                name: updatedReport.Name,
              }),
            );
            setSnackbarError(false);
            setIsTableActionPending(false);
          });
        })
        .catch((err) => {
          console.error(err);
        });
    } else {
      unstable_batchedUpdates(() => {
        setSnackbarMessage(t('SnackbarFailedToUpdateItem', { item: t('Report') }));
        setSnackbarError(true);
        setIsTableActionPending(false);
      });
    }
  };

  const handleApplySelect = (value: Set<string | number>, key: string | number): void => {
    if (
      selectedFilters[key].size === value.size &&
      [...selectedFilters[key]].every((item) => value.has(item))
    ) {
      // No change, do nothing
      return;
    } else {
      const clonedSelectedFilters = { ...selectedFilters };
      clonedSelectedFilters[key] = value;
      setSelectedFilters(clonedSelectedFilters);
      cfg.filters[key].selectedOptionIDs = value;
      track('Reporting List - Use Filter', { isSuperUser, filter: key }, user.user);
    }
  };

  const handleClearFilters = (): void => {
    const clearedSelectedFilters: Record<string, Set<string | number>> = {};
    Object.values(cfg.filters).forEach((filter) => {
      const newValue = new Set<string | number>(filter.selectedOptionIDsOnReset || []);
      clearedSelectedFilters[filter.key] = newValue;
      cfg.filters[filter.key].selectedOptionIDs = newValue;
    });
    setSelectedFilters(clearedSelectedFilters);
  };

  const handleDeleteItem = (id: string): Promise<unknown> => {
    setIsTableActionPending(true);
    deleteReport({
      query: {
        publisher_id: session.publisher.ID,
        report_id: id,
      },
    })
      .then(() => {
        track('Reporting List - Delete Report', { isSuperUser }, user.user);
        unstable_batchedUpdates(() => {
          setIsTableActionPending(false);
          setResponseData((prevData) => prevData.filter((report) => report.ID !== id));
          setSnackbarMessage(t('SnackbarSuccessfullyDeletedItem', { item: t('Report') }));
          setSnackbarError(false);
        });
      })
      .catch((err) => {
        console.error(err);
        unstable_batchedUpdates(() => {
          setIsTableActionPending(false);
          setSnackbarMessage(t('SnackbarFailedToDeleteItem', { item: t('Report') }));
          setSnackbarError(false);
        });
      });
    return new Promise((resolve) => resolve(undefined));
  };
  const handleCloneItem = (id: string): void => {
    const selectedReport = responseData.find((report) => report.ID === id);
    if (selectedReport) {
      const clonedReport = {
        ...selectedReport,
        ID: undefined,
        Name: `${t('Cloned')} ${selectedReport.Name}`,
        CreatedAt: undefined,
        UpdatedAt: undefined,
        DeletedAt: undefined,
      };
      createReport({
        query: {
          publisher_id: session.publisher.ID,
          report: clonedReport,
        },
      })
        .then((newReport) => {
          track('Reporting List - Clone Report', { isSuperUser }, user.user);
          navigate(RouteReportExisting, extractRouteParams({ session, user }), {
            reportID: newReport.ID,
          });
        })
        .catch((err) => {
          console.error(err);
          unstable_batchedUpdates(() => {
            setSnackbarMessage(t('SnackbarFailedToCloneItem', { item: t('Report') }));
            setSnackbarError(true);
          });
        });
    } else {
      unstable_batchedUpdates(() => {
        setSnackbarMessage(t('SnackbarFailedToCloneItem', { item: t('Report') }));
        setSnackbarError(true);
      });
    }
  };

  const getReportSettingsRoute = (id: string): string => {
    return getRoutePath(RouteReportExisting, extractRouteParams({ session, user }), {
      reportID: id,
    });
  };

  useEffect(() => {
    queryReports({
      query: {
        publisher_id: session.publisher.ID,
      },
    })
      .then((reports) => {
        populateFiltersAndData(reports);
      })
      .catch((err) => {
        console.error(err);
        unstable_batchedUpdates(() => {
          setLoadState(LoadingStateNoData);
          setResponseData([]);
          setFiltersLoadState(LoadingStateNoData);
          setFilters({});
          setSnackbarError(true);
          setSnackbarMessage(t('SnackbarErrorRetrievingData'));
        });
      });
  }, []);

  useEffect(() => {
    if (!isEmpty(responseData)) {
      let tempFilteredData = [...responseData];
      // Apply all filters cumulatively
      tempFilteredData = tempFilteredData.filter((report) => {
        // Check each filter condition
        for (const [filterKey, selectedOptionIDs] of Object.entries(selectedFilters)) {
          const filterConfig = cfg.filters[filterKey];
          if (!filterConfig || isEmpty(selectedOptionIDs)) {
            continue; // Skip if no filter config or no selections
          }

          let passesFilter = false;

          if (filterKey === 'User') {
            // Filter by User ID
            if (selectedOptionIDs.has('-1')) {
              // All Users - show all reports
              passesFilter = true;
            } else if (selectedOptionIDs.has('0') && report.User?.RoleID === ROLE_ID_SUPER) {
              // Publica Admin
              passesFilter = true;
            } else if (report.User?.ID && selectedOptionIDs.has(report.User.ID)) {
              // Specific users
              passesFilter = true;
            }
          } else if (filterKey === 'Schedule') {
            // Filter by Schedule
            const isScheduled = report.Active === 'true';
            passesFilter =
              selectedOptionIDs.has(0) || // Show All
              (selectedOptionIDs.has(2) && isScheduled) || // Scheduled
              (selectedOptionIDs.has(1) && !isScheduled); // Not Scheduled
          } else if (filterKey === 'ReportType') {
            // Filter by Report Type ID
            passesFilter = selectedOptionIDs.has(report.ReportType);
          }

          // If this filter fails, the report should be excluded
          if (!passesFilter) {
            return false;
          }
        }

        // If all filters pass, include the report
        return true;
      });
      setFilteredData(tempFilteredData);
    }
  }, [responseData, selectedFilters]);

  useEffect(() => {
    if (!isEmpty(filteredData)) {
      const queriedData = queryFilteredData(filteredData, query, searchableColumnKeys);
      setQueriedData(queriedData);
    } else {
      setQueriedData([]);
    }
  }, [filteredData, query]);

  useEffect(() => {
    if (!isEmpty(queriedData)) {
      const rows = formatReportsResponseIntoLVTRows(queriedData, columns);
      unstable_batchedUpdates(() => {
        setTotalRowCount(rows.length);
        setSortedRows(stableSort(rows, getComparator(order, orderBy, columns[orderBy].valueType)));
      });
    } else {
      unstable_batchedUpdates(() => {
        setTotalRowCount(0);
        setSortedRows([]);
      });
    }
  }, [queriedData, order, orderBy, columns]);

  useEffect(() => {
    if (!isEmpty(sortedRows)) {
      unstable_batchedUpdates(() => {
        setVisibleRows(sortedRows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage));
        setLoadState(LoadingStateHasData);
      });
    } else {
      unstable_batchedUpdates(() => {
        setVisibleRows([]);
        setLoadState(LoadingStateNoData);
      });
    }
  }, [sortedRows, page, rowsPerPage]);

  // this is to ensure that the default state is updated when the component is mounted
  useEffect(() => {
    if (isMounted.current) {
      defaultState.order = order;
      defaultState.orderBy = orderBy;
      defaultState.page = page;
      defaultState.rowsPerPage = rowsPerPage;
    }
    isMounted.current = true;
  }, [order, orderBy, page, rowsPerPage]);

  return (
    <ReportingListContext.Provider
      value={{
        cfg,
        readAuthorization,
        writeAuthorization,
      }}
    >
      <Panels
        max
        vertical
        relative
        minWidthMain
        largeHorizontalPadding
        style={{ rowGap: spacing.small }}
      >
        <Panel smallVerticalPadding>
          <TopBar title='Reporting' enableUINotices tKey={'Reporting'} />
        </Panel>
        {/* TOP BUTTONS */}
        <Panel>
          <Cols
            wrap
            gap={spacing.small}
            style={{
              justifyContent: 'flex-end',
              alignItems: 'center',
              rowGap: spacing.small,
            }}
          >
            <Col>
              <ButtonV2
                disabled={!writeAuthorization}
                onClick={() => {
                  track('Report List - Create Report', { isSuperUser }, user.user);
                  navigate(RouteReportNew, extractRouteParams({ session, user }));
                }}
                className={`cypress-${cypressClassName}-button-createreport`}
                tKey='CreateItem'
                tProps={{ item: t('Report') }}
              />
            </Col>
          </Cols>
        </Panel>
        {/* FILTERS */}
        <Panel highlighted>
          <FilterRowV2
            cypressParentName={cypressClassName}
            enableClear={true}
            filtersConfig={cfg.filters}
            filtersToOptionsMap={filters}
            handleApplySelect={handleApplySelect}
            handleClearFilters={handleClearFilters}
            selectedFilters={selectedFilters}
            loadState={filtersLoadState}
          />
        </Panel>
        <Panel highlighted marginTop={spacing.base} marginBottom={spacing.base} smallPadding>
          <Rows>
            <Row>
              <Cols style={{ justifyContent: 'space-between', alignItems: 'center' }}>
                {/* LEFT SIDE */}
                <Col>
                  <Cols
                    wrap
                    gap={spacing.medium}
                    style={{
                      alignItems: 'center',
                      rowGap: spacing.small,
                    }}
                  >
                    <Col>
                      <TextInput
                        enableClear
                        handleOnChange={handleOnChangeQuery}
                        msDelay={500}
                        placeholder={t('SearchByNameID')}
                        prefix='search'
                        inputID='reporting-list-search-input'
                      />
                    </Col>
                    <Col>
                      <SearchResultsText
                        numRowsDisplayed={visibleRows.length}
                        numTotalRows={totalRowCount}
                      />
                    </Col>
                  </Cols>
                </Col>
                {/* RIGHT SIDE */}
                <Col></Col>
              </Cols>
            </Row>
            <Row>
              <ListViewTable
                activeColumnKeys={cfg.activeColumnKeys}
                columns={columns}
                customProps={{ reportTypeMap }}
                displayNameWithID={!isDemoUser}
                enableActions
                enableStatusSwitch
                getItemSettingsRoute={getReportSettingsRoute}
                handleChangePage={handleChangePage}
                handleChangeRowsPerPage={handleChangeRowsPerPage}
                handleCloneItem={handleCloneItem}
                handleDeleteItem={handleDeleteItem}
                handleOnChangeStatus={handleOnChangeStatus}
                handleOnClickColumnHeader={handleOnClickColumnHeader}
                isActionPending={isTableActionPending}
                isReadAuthorized={readAuthorization.isAuthorized}
                isSuperUser={isSuperUser}
                isWriteAuthorized={writeAuthorization.isAuthorized}
                loadState={loadState}
                order={order}
                orderBy={orderBy}
                page={page}
                rows={visibleRows}
                rowsPerPage={rowsPerPage}
                totalRowCount={totalRowCount}
                className={cypressClassName}
              />
            </Row>
          </Rows>
        </Panel>
        <SnackbarView
          open={!!snackbarMessage}
          error={!!snackbarError}
          onClose={handleSnackbarClose}
        >
          {snackbarMessage}
        </SnackbarView>
        <PanelBreak height={spacing.large} />
      </Panels>
    </ReportingListContext.Provider>
  );
};
